import { supabase, getStoragePublicUrl } from "../../lib/supabaseClient";
import { getAdminScopeUserId } from "../../config/admin";
import { mapProfile, mapProfileToDb, mapImageRow, normalizeUserPayload } from "./mappers";
import { filterExploreProfiles } from "../../util/profileQuality";

function requireClient() {
  if (!supabase) throw new Error("Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.");
  return supabase;
}

function mapInterestName(name) {
  return name ? { interestName: name } : null;
}

async function fetchInterestsForUser(userId) {
  const { data, error } = await requireClient()
    .from("user_interests")
    .select("interests(name)")
    .eq("user_id", userId);
  if (error) throw error;
  return (data || []).map((row) => mapInterestName(row.interests?.name)).filter(Boolean);
}

async function fetchInterestsForUsers(userIds) {
  if (!userIds?.length) return new Map();
  const { data, error } = await requireClient()
    .from("user_interests")
    .select("user_id, interests(name)")
    .in("user_id", userIds);
  if (error) throw error;

  const byUser = new Map();
  for (const row of data || []) {
    const interest = mapInterestName(row.interests?.name);
    if (!interest) continue;
    const list = byUser.get(row.user_id) ?? [];
    list.push(interest);
    byUser.set(row.user_id, list);
  }
  return byUser;
}

export async function GetUserById(id) {
  const { data, error } = await requireClient()
    .from("profiles")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;

  const user = mapProfile(data);
  const { data: images } = await requireClient()
    .from("profile_images")
    .select("*")
    .eq("user_id", id)
    .order("sort_order");
  user.images = (images || []).map((img) => ({
    ...mapImageRow(img),
    url: getStoragePublicUrl(img.storage_path) ?? resolveAzureUrl(img.file_name),
  }));
  user.interests = await fetchInterestsForUser(id);
  return user;
}

async function adminUpdateProfile(userId, payload) {
  const adminId = getAdminScopeUserId();
  if (!adminId) throw new Error("Admin session required");
  const { invokeAdminUsers } = await import("./subscriptions");
  await invokeAdminUsers("updateUser", { adminId, userId, payload });
}

export async function CreateUser(user) {
  const db = mapProfileToDb(user);
  const adminId = getAdminScopeUserId();
  if (adminId) {
    const { invokeAdminUsers } = await import("./subscriptions");
    await invokeAdminUsers("createUser", { adminId, payload: db });
    return mapProfile({ ...db, first_name: db.first_name, last_name: db.last_name });
  }
  const { data, error } = await requireClient()
    .from("profiles")
    .upsert(db, { onConflict: "id" })
    .select("*")
    .single();
  if (error) throw error;
  return mapProfile(data);
}

export async function UpdateUser(user) {
  const normalized = normalizeUserPayload(user);
  const userId = normalized.userId ?? user.id;
  if (!userId) throw new Error("userId required");

  const existing = await GetUserById(userId);
  const currentPrefs = existing?.preferences ?? {};

  const db = {
    ...(normalized.bio != null ? { bio: normalized.bio } : {}),
    ...(normalized.country != null ? { country: normalized.country } : {}),
    ...(normalized.education != null ? { education: normalized.education } : {}),
    ...(normalized.birthDate != null ? { birth_date: normalized.birthDate } : {}),
    ...(normalized.profileImageUri != null ? { profile_image_uri: normalized.profileImageUri } : {}),
    ...(normalized.isProfileComplete != null
      ? { is_profile_complete: normalized.isProfileComplete }
      : {}),
    ...(normalized.firstName != null ? { first_name: normalized.firstName } : {}),
    ...(normalized.lastName != null ? { last_name: normalized.lastName } : {}),
    ...(normalized.email != null ? { email: normalized.email } : {}),
    ...(normalized.isVerified != null ? { is_verified: normalized.isVerified } : {}),
    ...(normalized.termsAcceptedAt != null
      ? { terms_accepted_at: normalized.termsAcceptedAt }
      : {}),
    ...(normalized.ageAttestedAt != null ? { age_attested_at: normalized.ageAttestedAt } : {}),
    ...(normalized.termsVersion != null ? { terms_version: normalized.termsVersion } : {}),
    preferences: { ...currentPrefs, ...normalized.lifestyle },
  };

  if (getAdminScopeUserId()) {
    await adminUpdateProfile(userId, db);
    return GetUserById(userId);
  }

  const { data, error } = await requireClient()
    .from("profiles")
    .upsert({ id: userId, ...db }, { onConflict: "id" })
    .select("*")
    .single();
  if (error) throw error;
  return mapProfile(data);
}

export async function CompleteProfile(data) {
  const normalized = normalizeUserPayload(data);
  const userId = normalized.userId;
  if (!userId) throw new Error("userId required");

  const existing = await GetUserById(userId);
  const currentPrefs = existing?.preferences ?? {};

  const row = {
    id: userId,
    bio: normalized.bio ?? existing?.bio ?? "",
    country: normalized.country ?? existing?.country ?? "",
    education: normalized.education ?? existing?.education ?? "",
    is_profile_complete: true,
    preferences: { ...currentPrefs, ...normalized.lifestyle },
  };
  if (normalized.birthDate) row.birth_date = normalized.birthDate;
  if (normalized.termsAcceptedAt) row.terms_accepted_at = normalized.termsAcceptedAt;
  if (normalized.ageAttestedAt) row.age_attested_at = normalized.ageAttestedAt;
  if (normalized.termsVersion) row.terms_version = normalized.termsVersion;

  const { data: saved, error } = await requireClient()
    .from("profiles")
    .upsert(row, { onConflict: "id" })
    .select("*")
    .single();
  if (error) throw error;

  if (Array.isArray(data.interests)) {
    await syncUserInterests(userId, data.interests);
  }
  return mapProfile(saved);
}

async function syncUserInterests(userId, interestNames) {
  const client = requireClient();
  const names = interestNames.filter(Boolean);
  const { data: allInterests } = await client.from("interests").select("*");
  const byName = new Map((allInterests || []).map((i) => [i.name.toLowerCase(), i.id]));

  for (const name of names) {
    if (!byName.has(name.toLowerCase())) {
      const { data: created } = await client.from("interests").insert({ name }).select("id").single();
      if (created) byName.set(name.toLowerCase(), created.id);
    }
  }

  await client.from("user_interests").delete().eq("user_id", userId);
  const rows = names
    .map((n) => byName.get(n.toLowerCase()))
    .filter(Boolean)
    .map((interestId) => ({ user_id: userId, interest_id: interestId }));
  if (rows.length) await client.from("user_interests").insert(rows);
}

export async function GetUserAccountStatus(userId) {
  const { data, error } = await requireClient()
    .from("profiles")
    .select("is_active")
    .eq("id", userId)
    .maybeSingle();
  if (error) throw error;
  return { status: data?.is_active !== false };
}

export const RECOMMENDATIONS_PAGE_SIZE = 40;

/**
 * @param {string} userId
 * @param {{ limit?: number, offset?: number }} [options]
 * @returns {Promise<{ items: any[], fetchedCount: number, hasMore: boolean }>}
 */
export async function GetUserRecommendations(userId, options = {}) {
  const limit = Number.isFinite(options.limit) ? options.limit : RECOMMENDATIONS_PAGE_SIZE;
  const offset = Number.isFinite(options.offset) ? Math.max(0, options.offset) : 0;
  const seed = typeof options.seed === "string" && options.seed.trim() ? options.seed.trim() : null;

  const viewer = await GetUserById(userId);
  const { data, error } = await requireClient().rpc("get_recommendations", {
    p_user_id: userId,
    p_limit: limit,
    p_offset: offset,
    p_seed: seed,
  });
  if (error) throw error;
  const list = Array.isArray(data) ? data : [];
  const mapped = list.map((item) => {
    const images = (item.images || []).map((img) => ({
      ...mapImageRow(img),
      url: getStoragePublicUrl(img.storagePath ?? img.storage_path) ?? resolveAzureUrl(img.fileName),
    }));
    const user = mapProfile(item.user);
    return {
      ...item,
      user: { ...user, images },
      images,
    };
  });
  const viewerGender = viewer?.gender ?? viewer?.preferences?.gender;
  const filtered = filterExploreProfiles(mapped, viewerGender);
  const keptIds = new Set(filtered.map((item) => item.user?.id).filter(Boolean));
  // RPC is looser than Discover UI (e.g. Google letter avatars). Callers must
  // exclude these or offset-0 pagination gets stuck on the same unusable page.
  const rejectedIds = mapped
    .map((item) => item.user?.id)
    .filter((id) => id && !keptIds.has(id));
  const userIds = filtered.map((item) => item.user?.id).filter(Boolean);
  const interestsByUser = await fetchInterestsForUsers(userIds);
  const items = filtered.map((item) => ({
    ...item,
    user: {
      ...item.user,
      interests: interestsByUser.get(item.user.id) ?? [],
    },
  }));
  return {
    items,
    rejectedIds,
    fetchedCount: list.length,
    hasMore: list.length >= limit,
  };
}

function resolveAzureUrl(fileName) {
  if (!fileName) return null;
  if (fileName.startsWith("http")) return fileName;
  const base = process.env.REACT_APP_Image_Url || "https://payinapoapistorage.blob.core.windows.net/images/";
  return `${base}${fileName}`;
}

export async function UpdateUserLocation(data) {
  const updates = {
    latitude: data.lat ?? data.latitude ?? null,
    longitude: data.long ?? data.longitude ?? null,
  };
  const country = typeof data.country === "string" ? data.country.trim() : "";
  if (country) updates.country = country;

  const { error } = await requireClient()
    .from("profiles")
    .update(updates)
    .eq("id", data.userId ?? data.id);
  if (error) throw error;
  return { success: true };
}

export async function UpdateUserPreferences(data) {
  const userId = data.userId ?? data.id;
  const { data: row, error } = await requireClient()
    .from("profiles")
    .update({ preferences: data.preferences ?? data })
    .eq("id", userId)
    .select("*")
    .single();
  if (error) throw error;
  return mapProfile(row);
}

export async function GetUserPreferences(userId) {
  const { data, error } = await requireClient()
    .from("profiles")
    .select("preferences")
    .eq("id", userId)
    .maybeSingle();
  if (error) throw error;
  return data?.preferences ?? {};
}

export async function DeleteUser(userId) {
  const adminId = getAdminScopeUserId();
  // Admin deleting someone else → service-role edge function
  if (adminId && String(userId) !== String(adminId)) {
    const { invokeAdminUsers } = await import("./subscriptions");
    return invokeAdminUsers("deleteUser", { adminId, userId });
  }
  // Self-delete under RLS (requires authenticated session)
  const { error } = await requireClient().from("profiles").delete().eq("id", userId);
  if (error) throw error;
  return { success: true };
}

export async function DeactivateUser(userId) {
  const { invokeAdminUsers } = await import("./subscriptions");
  const adminId = getAdminScopeUserId();
  if (!adminId) throw new Error("Admin session required");
  return invokeAdminUsers("deactivateUser", { adminId, userId });
}

export async function ActivateUser(userId) {
  const { invokeAdminUsers } = await import("./subscriptions");
  const adminId = getAdminScopeUserId();
  if (!adminId) throw new Error("Admin session required");
  return invokeAdminUsers("activateUser", { adminId, userId });
}

export async function UpdateCountry(userId, country) {
  const value = typeof country === "string" ? country : JSON.stringify(country);
  if (getAdminScopeUserId()) {
    await adminUpdateProfile(userId, { country: value });
    return { success: true };
  }
  const { error } = await requireClient().from("profiles").update({ country: value }).eq("id", userId);
  if (error) throw error;
  return { success: true };
}

export async function GetAllUsers(adminUserId) {
  if (adminUserId) {
    const { invokeAdminUsers } = await import("./subscriptions");
    const rows = await invokeAdminUsers("listUsers", { adminId: adminUserId });
    return (rows || []).map(mapProfile);
  }
  const client = requireClient();
  const pageSize = 1000;
  const rows = [];
  let from = 0;

  // PostgREST max_rows is 1000 — paginate until the last short page.
  for (;;) {
    const to = from + pageSize - 1;
    const { data, error } = await client
      .from("profiles")
      .select("*")
      .order("created_at", { ascending: false })
      .order("id", { ascending: false })
      .range(from, to);
    if (error) throw error;
    const batch = data || [];
    rows.push(...batch);
    if (batch.length < pageSize) break;
    from += pageSize;
  }

  return rows.map(mapProfile);
}

export async function signInWithGoogle() {
  const { data, error } = await requireClient().auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo: `${window.location.origin}/` },
  });
  if (error) throw error;
  return data;
}

export async function getSession() {
  const { data, error } = await requireClient().auth.getSession();
  if (error) throw error;
  return data.session;
}

export async function signOutSupabase() {
  await requireClient().auth.signOut();
}

export async function upsertProfileFromSession(session) {
  if (!session?.user) return null;
  const meta = session.user.user_metadata ?? {};
  const user = {
    id: session.user.id,
    email: session.user.email,
    firstName: meta.given_name ?? meta.full_name?.split(" ")[0] ?? "",
    lastName: meta.family_name ?? meta.full_name?.split(" ").slice(1).join(" ") ?? "",
    profileImageUri: null,
    isProfileComplete: false,
    isVerified: false,
  };
  return CreateUser(user);
}

export async function RecordComplianceAttestation(userId, attestation) {
  if (!userId) throw new Error("userId required");
  const payload = {
    terms_accepted_at: attestation.termsAcceptedAt ?? new Date().toISOString(),
    age_attested_at: attestation.ageAttestedAt ?? new Date().toISOString(),
    terms_version: attestation.termsVersion ?? null,
  };
  const { data, error } = await requireClient()
    .from("profiles")
    .upsert({ id: userId, ...payload }, { onConflict: "id" })
    .select("*")
    .single();
  if (error) throw error;
  return mapProfile(data);
}

export async function BlockUser(blockerId, blockedId) {
  if (!blockerId || !blockedId) throw new Error("blockerId and blockedId required");
  if (blockerId === blockedId) throw new Error("Cannot block yourself");
  const { error } = await requireClient().from("user_blocks").upsert(
    { blocker_id: blockerId, blocked_id: blockedId },
    { onConflict: "blocker_id,blocked_id" }
  );
  if (error) throw error;
  return { success: true };
}

export async function UnblockUser(blockerId, blockedId) {
  const { error } = await requireClient()
    .from("user_blocks")
    .delete()
    .eq("blocker_id", blockerId)
    .eq("blocked_id", blockedId);
  if (error) throw error;
  return { success: true };
}

export async function GetBlockedUserIds(blockerId) {
  if (!blockerId) return [];
  const { data, error } = await requireClient()
    .from("user_blocks")
    .select("blocked_id")
    .eq("blocker_id", blockerId);
  if (error) throw error;
  return (data || []).map((row) => row.blocked_id).filter(Boolean);
}

export async function ReportUser({ reporterId, reportedId, reason, details = "" }) {
  if (!reporterId || !reportedId) throw new Error("reporterId and reportedId required");
  if (reporterId === reportedId) throw new Error("Cannot report yourself");
  const { data, error } = await requireClient()
    .from("user_reports")
    .insert({
      reporter_id: reporterId,
      reported_id: reportedId,
      reason,
      details: details || "",
      status: "open",
    })
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

export async function ListUserReports({ limit = 100 } = {}) {
  const { data, error } = await requireClient()
    .from("user_reports")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

export async function UpdateReportStatus(reportId, status) {
  const { data, error } = await requireClient()
    .from("user_reports")
    .update({ status })
    .eq("id", reportId)
    .select("*")
    .single();
  if (error) throw error;
  return data;
}
